import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import CreditsBadge from '../components/CreditsBadge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  User,
  CreditCard,
  BarChart3,
  Clock,
  AlertTriangle,
  Check,
  Loader2,
  ExternalLink,
  LogOut,
  Zap,
  Coins,
  Plus,
  Settings,
  Users,
  Mail,
  Copy,
  Trash2,
  UserPlus,
  Link as LinkIcon
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  
  // Team management state
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');

  useEffect(() => {
    // Only fetch usage once auth is ready and user exists
    if (!authLoading && user) {
      fetchUsage();
      fetchCreditHistory();
      fetchTeamInfo();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);

  const fetchUsage = async () => {
    try {
      const response = await api.get('/api/user/usage');
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      toast.error('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get('/api/credits/history?limit=50');
      setCreditHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchTeamInfo = async () => {
    try {
      setTeamLoading(true);
      const response = await api.get('/api/team');
      setTeamInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch team info:', error);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleInviteTeamMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await api.post('/api/team/invite', { email: inviteEmail.trim() });
      toast.success(response.data.message);
      setLastInviteLink(response.data.invite_link);
      setInviteEmail('');
      fetchTeamInfo(); // Refresh team info
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (lastInviteLink) {
      await navigator.clipboard.writeText(lastInviteLink);
      toast.success('Invitation link copied to clipboard!');
    }
  };

  const handleRemoveTeamMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await api.delete(`/api/team/members/${userId}`);
      toast.success('Team member removed');
      fetchTeamInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/api/team/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchTeamInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing/subscription');
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post('/api/billing/portal', null, {
        params: { origin_url: window.location.origin }
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    setCancelling(true);
    try {
      await api.post('/api/subscription/cancel');
      toast.success('Your subscription has been cancelled. You will have access until the end of your billing period.');
      setCancelDialogOpen(false);
      fetchUsage();
    } catch (error) {
      console.error('Failed to cancel:', error);
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilReset = () => {
    if (!usage?.reset_date) return null;
    const reset = new Date(usage.reset_date);
    const now = new Date();
    const diff = Math.ceil((reset - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const planNames = {
    free: 'Free',
    pro: 'Pro',
    scale: 'Scale',
    business: 'Business',
    // Legacy names
    standard: 'Pro',
    enterprise: 'Business'
  };

  // Prices must match backend/plans.py PLAN_LIMITS - NEW PRICING STRUCTURE
  const planPrices = {
    free: { monthly: 0, yearly: 0 },
    pro: { monthly: 29, yearly: 290 },
    scale: { monthly: 79, yearly: 790 },
    business: { monthly: 199, yearly: 1990 },
    // Legacy plans mapping
    standard: { monthly: 29, yearly: 290 },
    enterprise: { monthly: 199, yearly: 1990 }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Use display_plan if available (maps legacy plans like enterprise -> business)
  const currentPlan = usage?.display_plan || usage?.plan || 'free';
  const rawPlan = usage?.plan || 'free'; // Keep raw plan for checking team features
  const billingCycle = usage?.billing_cycle || 'monthly';
  const price = planPrices[currentPlan]?.[billingCycle] || planPrices[rawPlan]?.[billingCycle] || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <CreditsBadge />
              <Button 
                onClick={() => navigate('/upload')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-10 h-10 rounded-full p-0">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-indigo-600" />
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-slate-900">{user?.name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/credits')}>
                    <Coins className="w-4 h-4 mr-2" />
                    Buy Credits
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="text-3xl font-heading font-bold text-slate-900 mb-8">Account & Billing</h1>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Account Info */}
          <div className="md:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
                  ) : (
                    <User className="w-7 h-7 text-indigo-600" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user?.name || 'User'}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Current Plan Card */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="text-indigo-200 text-sm font-medium">Current Plan</span>
                <Zap className="w-5 h-5 text-yellow-300" />
              </div>
              <h3 className="text-2xl font-bold mb-1">{planNames[currentPlan]}</h3>
              {price > 0 ? (
                <p className="text-indigo-200">
                  ${price}/{billingCycle === 'yearly' ? 'year' : 'month'}
                </p>
              ) : (
                <p className="text-indigo-200">Free forever</p>
              )}
              
              {currentPlan !== 'business' && rawPlan !== 'enterprise' && (
                <Button 
                  onClick={handleUpgrade}
                  className="w-full mt-4 bg-white text-indigo-600 hover:bg-indigo-50"
                  data-testid="upgrade-btn"
                >
                  Upgrade Plan
                </Button>
              )}
            </div>

            {/* Usage This Month - Below Current Plan, only for subscribers */}
            {currentPlan !== 'free' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                  <h2 className="text-base font-semibold text-slate-900">Usage This Month</h2>
                </div>
                {usage?.reset_date && (
                  <span className="text-xs text-slate-500">
                    Resets in {getDaysUntilReset()} days
                  </span>
                )}
              </div>

              {/* Row Usage with Color Coding */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Rows Cleaned</span>
                  <span className={`text-sm font-medium ${
                    (usage?.rows?.percentage || 0) > 90 ? 'text-red-600' :
                    (usage?.rows?.percentage || 0) > 65 ? 'text-orange-600' :
                    'text-emerald-600'
                  }`}>
                    {(usage?.rows?.used || 0).toLocaleString()} / {typeof usage?.rows?.limit === 'number' ? usage.rows.limit.toLocaleString() : usage?.rows?.limit}
                  </span>
                </div>
                <Progress 
                  value={usage?.rows?.percentage || 0} 
                  className={`h-2 ${
                    (usage?.rows?.percentage || 0) > 90 ? '[&>div]:bg-red-500' :
                    (usage?.rows?.percentage || 0) > 65 ? '[&>div]:bg-orange-500' :
                    '[&>div]:bg-emerald-500'
                  }`}
                />
                {(usage?.rows?.percentage || 0) > 65 && (
                  <div className={`flex items-center justify-between mt-2 p-2 rounded-lg ${
                    (usage?.rows?.percentage || 0) > 90 ? 'bg-red-50' : 'bg-orange-50'
                  }`}>
                    <p className={`text-xs flex items-center gap-1 ${
                      (usage?.rows?.percentage || 0) > 90 ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      <AlertTriangle className="w-3 h-3" />
                      {(usage?.rows?.percentage || 0) > 90 ? 'Almost at limit!' : 'Approaching limit'}
                    </p>
                    <Link to="/credits">
                      <Button size="sm" variant="ghost" className={`h-7 text-xs ${
                        (usage?.rows?.percentage || 0) > 90 ? 'text-red-600' : 'text-orange-600'
                      }`}>
                        Buy Credits
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Right Column - Credits & Billing */}
          <div className="md:col-span-2 space-y-6">
            {/* Credits Section - Compact on mobile */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-4 sm:p-6 shadow-soft">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900">Credit Balance</h2>
                </div>
                <Link to="/credits">
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 h-8 text-xs sm:text-sm">
                    Buy Credits
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-baseline gap-2">
                <span className="text-2xl sm:text-4xl font-bold text-amber-600">
                  {(user?.credits_balance || 0).toLocaleString()}
                </span>
                <span className="text-sm text-slate-600">credits available</span>
              </div>
            </div>

            {/* Credit History */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Credit History</h2>
                </div>
                {creditHistory.length > 5 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowAllHistory(!showAllHistory)}
                  >
                    {showAllHistory ? 'Show Less' : 'View All'}
                  </Button>
                )}
              </div>

              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : creditHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Coins className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No credit history yet</p>
                  <p className="text-sm">Your credit transactions will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(showAllHistory ? creditHistory : creditHistory.slice(0, 5)).map((item, idx) => {
                    // Get display label for transaction type
                    const getTransactionLabel = (type, reason, planPurchased) => {
                      const labels = {
                        'signup_bonus': 'Welcome Credits',
                        'referral_signup': 'Referral Bonus (Friend Signed Up)',
                        'referral_purchase': `Referral Bonus (${planPurchased ? planPurchased.charAt(0).toUpperCase() + planPurchased.slice(1) + ' Plan' : 'Friend Subscribed'})`,
                        'purchase': 'Credit Pack Purchase',
                        'admin_grant': 'Admin Credit Grant',
                        'cleaning': 'Data Cleaning',
                        'subscription_renewal': 'Subscription Credits',
                        'refund': 'Credit Refund',
                        'promo': 'Promotional Credits'
                      };
                      return labels[type] || reason || type;
                    };

                    return (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            item.credits > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {item.credits > 0 ? (
                              <Zap className="w-4 h-4" />
                            ) : (
                              <BarChart3 className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {getTransactionLabel(item.type, item.reason, item.plan_purchased)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Unknown date'}
                            </p>
                          </div>
                        </div>
                        <div className={`font-semibold ${item.credits > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {item.credits > 0 ? '+' : ''}{item.credits?.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Billing Details */}
            {currentPlan !== 'free' && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Billing Details</h2>
                </div>

                {/* Cancelled subscription warning */}
                {(usage?.subscription_cancelled || usage?.cancel_at_period_end) && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 text-amber-800 font-medium">
                      <AlertTriangle className="w-5 h-5" />
                      Subscription Cancelled
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      Your {planNames[currentPlan]} plan will end on{' '}
                      <span className="font-semibold">{formatDate(usage?.subscription_end_date)}</span>.
                      You&apos;ll have access until then, and can resubscribe anytime.
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Plan</span>
                    <span className="font-medium text-slate-900">{planNames[currentPlan]}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Billing Frequency</span>
                    <span className="font-medium text-slate-900 capitalize">{billingCycle}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Price</span>
                    <span className="font-medium text-slate-900">${price}/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">
                      {(usage?.subscription_cancelled || usage?.cancel_at_period_end) ? 'Plan Ends On' : 'Next Billing Date'}
                    </span>
                    <span className="font-medium text-slate-900">
                      {formatDate((usage?.subscription_cancelled || usage?.cancel_at_period_end) ? usage?.subscription_end_date : usage?.reset_date)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button 
                    variant="default"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Manage Subscription
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleUpgrade}
                  >
                    Change Plan
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Update payment method, view invoices, or cancel subscription
                </p>
              </div>
            )}

            {/* Team Management - Business/Enterprise Only */}
            {(rawPlan === 'enterprise' || rawPlan === 'business') && teamInfo?.is_team_owner && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-lg font-semibold text-slate-900">Team Management</h2>
                  </div>
                  <span className="text-sm text-slate-500">
                    {teamInfo?.current_members || 1} / {teamInfo?.max_users || 5} members
                  </span>
                </div>

                {/* Shared Usage Info */}
                <div className="bg-indigo-50 rounded-xl p-4 mb-6">
                  <p className="text-sm font-medium text-indigo-800 mb-2">Shared Team Quota</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-indigo-600">
                      {(teamInfo?.shared_usage?.rows_used || 0).toLocaleString()} / {(teamInfo?.shared_usage?.rows_limit || 100000).toLocaleString()} credits/month
                    </span>
                    <span className="text-xs text-indigo-500">
                      Shared across all {teamInfo?.current_members || 1} members
                    </span>
                  </div>
                  <Progress 
                    value={((teamInfo?.shared_usage?.rows_used || 0) / (teamInfo?.shared_usage?.rows_limit || 100000)) * 100} 
                    className="h-2 mt-2"
                  />
                </div>

                {/* Invite New Member */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <UserPlus className="w-4 h-4 inline mr-2" />
                    Invite Your Coworkers
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1"
                      data-testid="team-invite-email-input"
                    />
                    <Button 
                      onClick={handleInviteTeamMember}
                      disabled={inviting || (teamInfo?.current_members + (teamInfo?.pending_invitations?.length || 0)) >= 5}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="team-invite-btn"
                    >
                      {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      {inviting ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Invitations will be sent by email. You can also share the unique link after sending.
                  </p>
                  
                  {/* Show last invite link */}
                  {lastInviteLink && (
                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-700">Invitation sent!</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyInviteLink}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pending Invitations */}
                {teamInfo?.pending_invitations?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Pending Invitations</h3>
                    <div className="space-y-2">
                      {teamInfo.pending_invitations.map((inv) => (
                        <div key={inv.invitation_id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-slate-700">{inv.email}</span>
                            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">Pending</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvitation(inv.invitation_id)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Current Team Members */}
                {teamInfo?.members?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Team Members</h3>
                    <div className="space-y-2">
                      {teamInfo.members.map((member) => (
                        <div key={member.user_id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-lg">
                          <div className="flex items-center gap-3">
                            {member.picture ? (
                              <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-indigo-600" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-slate-900">{member.name || 'Team Member'}</p>
                              <p className="text-xs text-slate-500">{member.email}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTeamMember(member.user_id)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(!teamInfo?.members?.length && !teamInfo?.pending_invitations?.length) && (
                  <div className="text-center py-6 text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No team members yet</p>
                    <p className="text-sm">Invite up to 4 coworkers to share your plan</p>
                  </div>
                )}
              </div>
            )}

            {/* Team Member View (non-owner) */}
            {teamInfo?.is_enterprise && !teamInfo?.is_team_owner && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Team Membership</h2>
                </div>
                <div className="bg-indigo-50 rounded-xl p-4">
                  <p className="text-sm text-indigo-800">
                    You are part of <strong>{teamInfo?.team_owner?.name || teamInfo?.team_owner?.email || 'a team'}</strong>&apos;s Enterprise team.
                  </p>
                  <p className="text-xs text-indigo-600 mt-2">
                    Your team shares 1M rows/month across all {teamInfo?.max_users || 5} members.
                  </p>
                </div>
              </div>
            )}

            {/* Free Plan CTA */}
            {currentPlan === 'free' && (
              <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-6 border border-indigo-100">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Unlock More Features</h3>
                <p className="text-slate-600 mb-4">
                  Upgrade to clean more files, process more rows, and save templates.
                </p>
                <Button onClick={handleUpgrade} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  View Plans
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your {planNames[currentPlan]} subscription?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>What happens when you cancel:</strong>
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• You&apos;ll keep access until the end of your billing period</li>
                <li>• Your data will be preserved</li>
                <li>• You can resubscribe anytime</li>
                <li>• After the period ends, you&apos;ll be on the Free plan</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelPlan}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Floating Support Button */}
      <SupportButton />
    </div>
  );
};

export default AccountPage;
